import React from 'react';
import { withFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import {
	FieldValueSubscriber,
	type FieldValueServiceActions,
} from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import type { TriggerIssueTransitionModalActions } from '@atlassian/jira-issue-transition-trigger/src/common/types.tsx';
import { useOpenIssueTransitionModal } from '@atlassian/jira-issue-transition-trigger/src/utils/use-trigger-issue-transition-modal/index.tsx';
import { useWorkflowTransitionsActions } from '@atlassian/jira-issue-workflow-transitions-services/src/main.tsx';
import type { WorkflowTransitionsActions } from '@atlassian/jira-issue-workflow-transitions-services/src/types.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAnalyticsSource } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { Props } from './types.tsx';
import StatusService from './status-service.tsx';

export const DIALOG_SUBMIT_BTN = '#issue-workflow-transition-submit';

export const safeUnbindDomEvent = (selector: string, event: string, handler: () => void): void => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const el = document.querySelector(selector);
	if (el) {
		el.removeEventListener(event, handler);
	}
};

export const safeBindDomEvent = (selector: string, event: string, handler: () => void): void => {
	safeUnbindDomEvent(selector, event, handler);

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const el = document.querySelector(selector);
	if (el) {
		el.addEventListener(event, handler);
	}
};

const StatusServiceWithoutAriId = (props: Props) => {
	const { issueId } = props;
	const cloudId = useCloudId();
	const ariIssueId = issueId
		? createAri({
				resourceOwner: 'jira',
				cloudId,
				resourceType: 'issue',
				resourceId: issueId,
			})
		: undefined;

	const renderIssueTransitionModal = useOpenIssueTransitionModal();

	return (
		<StatusService
			{...props}
			renderIssueTransitionModal={renderIssueTransitionModal}
			issueId={ariIssueId}
		/>
	);
};

const StatusServiceForMigrateIssueTransitionEnabled = (props: Props) => {
	const source = useAnalyticsSource();
	return <StatusServiceWithoutAriId {...props} source={source} />;
};

const StatusWithValueService = (
	props: Flow.Diff<
		Props,
		{
			fieldValueActions: FieldValueServiceActions;
			workflowTransitionsActions: WorkflowTransitionsActions;
			renderIssueTransitionModal?: TriggerIssueTransitionModalActions['openIssueTransitionModal'];
		}
	>,
) => {
	const [, workflowTransitionsActions] = useWorkflowTransitionsActions();
	return (
		<FieldValueSubscriber issueKey={props.issueKey} fieldKey={STATUS_TYPE}>
			{(value, fieldValueActions) => {
				const statusServiceProps = {
					// Overrides the value in the store if the consumer really wants to do it
					value,
					...props,
					fieldValueActions,
					workflowTransitionsActions,
				};

				return <StatusServiceForMigrateIssueTransitionEnabled {...statusServiceProps} />;
			}}
		</FieldValueSubscriber>
	);
};
const wrappedStatusService = withFlagService(StatusWithValueService);

export { wrappedStatusService as StatusService };
